import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Mail, Bird, Sun, Moon } from 'lucide-react';

interface PrivacyPolicyProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  language: 'pt' | 'en';
  setLanguage: (lang: 'pt' | 'en') => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ theme, toggleTheme, language, setLanguage }) => {

  const content = {
    pt: {
      pageTitle: "Política de Privacidade",
      back: "Voltar",
      subtitle: "Athena Studies • Parthenon Planner",
      intro: (<>A Athena Studies valoriza a sua privacidade e está comprometida com a proteção dos dados pessoais dos usuários do <strong>Parthenon Planner</strong>. Esta Política de Privacidade descreve de forma clara como coletamos, utilizamos, armazenamos e protegemos as suas informações, em conformidade com a legislação brasileira, especialmente a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).</>),
      introConsent: "Ao utilizar o Parthenon Planner, você concorda com as práticas descritas nesta Política.",
      sections: [
        {
          title: "1. Quem somos",
          paragraphs: [
            <>O <strong>Parthenon Planner</strong> é um aplicativo de organização e gestão de estudos desenvolvido pela <strong>Athena Studies</strong>, iniciativa educacional voltada a apoiar estudantes na construção de autonomia, organização e estratégia de aprendizagem.</>,
            <>Para fins desta Política, quando mencionamos "Athena", "Parthenon", "nós" ou "nosso", estamos nos referindo à Athena Studies, responsável pelo tratamento dos dados na qualidade de <strong>controladora</strong>, nos termos da LGPD.</>,
            <>O canal responsável pelo atendimento de demandas relacionadas à proteção de dados é o e-mail <strong>studiesathena2025@gmail.com</strong>, que funciona também como ponto de contato para o exercício dos direitos previstos nesta Política.</>
          ]
        },
        {
          title: "2. Dados que coletamos",
          intro: "Coletamos apenas os dados necessários para o funcionamento adequado do aplicativo.",
          dataBlocks: [
            {
              label: "2.1 Dados fornecidos pelo usuário",
              color: "teal",
              items: ["Nome", "Endereço de e-mail", "Senha (armazenada de forma criptografada — nunca em texto plano)"]
            },
            {
              label: "2.2 Dados de uso e conteúdo inserido",
              color: "coral",
              items: ["Matérias estudadas", "Tempo de estudo", "Datas, compromissos e tarefas", "Projetos acadêmicos", "Arquivos e materiais compartilhados voluntariamente pelo usuário"],
              footnote: "Os arquivos e materiais são armazenados exclusivamente para permitir o funcionamento das funcionalidades de organização e compartilhamento do aplicativo, e são retidos enquanto a conta permanecer ativa."
            },
            {
              label: "2.3 Dados técnicos",
              color: "gold",
              items: ["Endereço IP", "Tipo de dispositivo e navegador", "Registros de acesso e logs de uso"]
            }
          ],
          note: "O Parthenon Planner não coleta dados sensíveis, como CPF, dados bancários ou informações de saúde.",
          noteLabel: "Nota:"
        },
        {
          title: "3. Base legal do tratamento",
          intro: "O tratamento dos dados pessoais realizado pelo Parthenon Planner fundamenta-se nas seguintes bases legais previstas no art. 7º da LGPD:",
          listItems: [
            <><strong>Consentimento</strong> — fornecido pelo usuário no momento do cadastro e uso do aplicativo.</>,
            <><strong>Execução de contrato</strong> — para permitir o funcionamento das funcionalidades contratadas pelo usuário.</>,
            <><strong>Legítimo interesse</strong> — para melhoria contínua do serviço e segurança da plataforma, respeitados os direitos e expectativas do titular.</>,
            <><strong>Cumprimento de obrigação legal</strong> — quando aplicável por determinação judicial ou regulatória.</>
          ]
        },
        {
          title: "4. Finalidade do uso dos dados",
          purposeItems: ["Funcionamento do aplicativo", "Organização de rotinas e projetos", "Geração de relatórios e insights", "Integração estudante/professor", "Sincronização em nuvem", "Aprimoramento contínuo"],
          purposeFootnote: "Os dados não serão utilizados para finalidades incompatíveis com as descritas acima sem novo aviso e, quando necessário, novo consentimento do titular."
        },
        {
          title: "5. Compartilhamento de dados",
          highlight: "A Athena Studies não vende ou comercializa dados pessoais.",
          highlightSub: "Dados poderão ser compartilhados apenas nas seguintes situações:",
          listItems: [
            <><strong>Provedores de infraestrutura tecnológica</strong> — utilizamos serviços de terceiros para hospedagem, banco de dados e autenticação (como provedores de nuvem internacionais). Esses parceiros atuam como operadores de dados e estão contratualmente obrigados a proteger as informações nos mesmos padrões desta Política. A transferência internacional de dados, quando ocorrer, observará as garantias previstas no art. 33 da LGPD.</>,
            <><strong>Compartilhamento entre usuários</strong> — exclusivamente mediante autorização explícita do titular.</>,
            <><strong>Cumprimento de obrigações legais</strong> — em resposta a ordens judiciais ou determinações de autoridades competentes.</>
          ]
        },
        {
          title: "6. Armazenamento, segurança e retenção",
          paragraphs: [
            "Adotamos medidas técnicas e organizacionais para proteger os dados contra acessos não autorizados, perdas ou alterações indevidas, incluindo criptografia de dados em trânsito (HTTPS) e controle de acesso por credenciais.",
            "Os dados são retidos pelo tempo necessário para a prestação do serviço ou conforme exigido por obrigação legal. Após o encerramento da conta, os dados pessoais são eliminados em prazo razoável, salvo quando sua retenção for obrigatória por lei."
          ]
        },
        {
          title: "7. Direitos do usuário",
          intro: "Nos termos do art. 18 da LGPD, o titular dos dados tem direito a:",
          listItems: [
            "Confirmação da existência de tratamento de seus dados",
            "Acesso aos dados que possuímos sobre você",
            "Correção de dados incompletos, inexatos ou desatualizados",
            "Anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos",
            "Portabilidade dos dados a outro fornecedor de serviço",
            "Eliminação dos dados tratados com base no consentimento",
            "Informação sobre compartilhamento com terceiros",
            "Informação sobre a possibilidade de não fornecer consentimento e as consequências disso",
            "Revogação do consentimento a qualquer momento",
            "Oposição ao tratamento realizado em desconformidade com a LGPD"
          ],
          footnote: "Para exercer qualquer desses direitos, entre em contato pelo canal indicado na seção 12 desta Política. Responderemos em prazo razoável e compatível com a complexidade da solicitação."
        },
        {
          title: "8. Exclusão de conta",
          paragraphs: ["O usuário pode solicitar a exclusão de sua conta a qualquer momento pelo canal de contato indicado. Após a confirmação, os dados serão eliminados em prazo razoável, respeitando eventuais obrigações legais de retenção."]
        },
        {
          title: "9. Uso por menores de idade",
          paragraphs: [
            "O aplicativo é destinado a usuários a partir de 13 anos. O tratamento de dados de adolescentes (entre 13 e 17 anos) observará o melhor interesse do menor, nos termos dos arts. 14 e 17 da LGPD e do Estatuto da Criança e do Adolescente (ECA).",
            "Menores de 18 anos devem utilizar o aplicativo com conhecimento e supervisão de um responsável legal. A Athena Studies poderá solicitar confirmação do responsável quando necessário.",
            "O Parthenon Planner não é direcionado a crianças menores de 13 anos e não coleta dados dessas pessoas intencionalmente."
          ]
        },
        {
          title: "10. Cookies",
          intro: "Podemos utilizar cookies e tecnologias semelhantes para garantir o funcionamento adequado do serviço e melhorar a experiência do usuário. Os cookies utilizados se dividem em:",
          listItems: [
            <><strong>Cookies essenciais</strong> — necessários para o funcionamento do aplicativo (ex.: manutenção de sessão autenticada). Não podem ser desativados sem comprometer o serviço.</>,
            <><strong>Cookies analíticos</strong> — utilizados para compreender como os usuários interagem com o aplicativo e melhorar sua experiência. Podem ser recusados sem impacto funcional.</>
          ],
          footnote: "Não utilizamos cookies de terceiros para fins de publicidade ou rastreamento comercial."
        },
        {
          title: "11. Alterações nesta política",
          paragraphs: ["Esta Política pode ser atualizada periodicamente. Alterações relevantes serão comunicadas ao usuário por e-mail ou mediante aviso no aplicativo. A versão mais recente estará sempre disponível no site ou app."]
        },
        {
          title: "12. Contato",
          contactLabel: "Canal de Atendimento — Proteção de Dados (LGPD)"
        }
      ],
      lastUpdated: "Última atualização: Janeiro de 2026",
      footer: "© 2026 • DOMINE O SEU FUTURO"
    },
    en: {
      pageTitle: "Privacy Policy",
      back: "Back",
      subtitle: "Athena Studies • Parthenon Planner",
      intro: (<>Athena Studies values your privacy and is committed to protecting the personal data of <strong>Parthenon Planner</strong> users. This Privacy Policy clearly describes how we collect, use, store, and protect your information, in accordance with applicable law, including the Brazilian General Data Protection Law (LGPD — Law No. 13.709/2018).</>),
      introConsent: "By using Parthenon Planner, you agree to the practices described in this Policy.",
      sections: [
        {
          title: "1. Who we are",
          paragraphs: [
            <>The <strong>Parthenon Planner</strong> is a study organization and management app developed by <strong>Athena Studies</strong>, an educational initiative focused on helping students build autonomy, organization, and learning strategy.</>,
            <>For the purposes of this Policy, when we mention "Athena", "Parthenon", "we", or "our", we are referring to Athena Studies, the entity responsible for data processing in the capacity of <strong>data controller</strong> under the LGPD.</>,
            <>The channel responsible for handling data protection inquiries is <strong>studiesathena2025@gmail.com</strong>, which also serves as the point of contact for exercising the rights described in this Policy.</>
          ]
        },
        {
          title: "2. Data we collect",
          intro: "We collect only the data necessary for the proper functioning of the application.",
          dataBlocks: [
            {
              label: "2.1 Data provided by the user",
              color: "teal",
              items: ["Name", "Email address", "Password (stored in encrypted form — never as plain text)"]
            },
            {
              label: "2.2 Usage data and inserted content",
              color: "coral",
              items: ["Subjects studied", "Study time", "Dates, commitments, and tasks", "Academic projects", "Files and materials voluntarily shared by the user"],
              footnote: "Files and materials are stored solely to enable the app's organization and sharing features, and are retained while the account remains active."
            },
            {
              label: "2.3 Technical data",
              color: "gold",
              items: ["IP address", "Device type and browser", "Access logs and usage records"]
            }
          ],
          note: "Parthenon Planner does not collect sensitive data such as tax IDs, banking information, or health data.",
          noteLabel: "Note:"
        },
        {
          title: "3. Legal basis for processing",
          intro: "The processing of personal data by Parthenon Planner is based on the following legal grounds set out in Article 7 of the LGPD:",
          listItems: [
            <><strong>Consent</strong> — provided by the user at the time of registration and use of the application.</>,
            <><strong>Performance of a contract</strong> — to enable the features the user has signed up for.</>,
            <><strong>Legitimate interest</strong> — for continuous service improvement and platform security, with full respect for the data subject's rights and expectations.</>,
            <><strong>Compliance with a legal obligation</strong> — where required by court order or regulatory authority.</>
          ]
        },
        {
          title: "4. Purpose of data use",
          purposeItems: ["App functionality", "Routine and project organization", "Reports and insights generation", "Student/teacher integration", "Cloud synchronization", "Continuous improvement"],
          purposeFootnote: "Data will not be used for purposes incompatible with those described above without prior notice and, where required, renewed consent from the data subject."
        },
        {
          title: "5. Data sharing",
          highlight: "Athena Studies does not sell or trade personal data.",
          highlightSub: "Data may be shared only in the following situations:",
          listItems: [
            <><strong>Technology infrastructure providers</strong> — we use third-party services for hosting, database, and authentication (including international cloud providers). These partners act as data processors and are contractually bound to protect information to the same standards as this Policy. Any international data transfers will comply with the safeguards required under Article 33 of the LGPD.</>,
            <><strong>Sharing between users</strong> — exclusively upon the data subject's explicit authorization.</>,
            <><strong>Compliance with legal obligations</strong> — in response to court orders or requests from competent authorities.</>
          ]
        },
        {
          title: "6. Storage, security, and retention",
          paragraphs: [
            "We adopt technical and organizational measures to protect data against unauthorized access, loss, or improper alteration, including encryption of data in transit (HTTPS) and credential-based access controls.",
            "Data is retained for as long as necessary to provide the service or as required by law. Following account closure, personal data is deleted within a reasonable timeframe, except where retention is legally required."
          ]
        },
        {
          title: "7. User rights",
          intro: "Under Article 18 of the LGPD, data subjects have the right to:",
          listItems: [
            "Confirmation of whether their data is being processed",
            "Access to the data we hold about you",
            "Correction of incomplete, inaccurate, or outdated data",
            "Anonymization, blocking, or deletion of unnecessary or excessive data",
            "Portability of data to another service provider",
            "Deletion of data processed on the basis of consent",
            "Information about sharing with third parties",
            "Information about the possibility of withholding consent and the consequences of doing so",
            "Withdrawal of consent at any time",
            "Objection to processing carried out in non-compliance with the LGPD"
          ],
          footnote: "To exercise any of these rights, please contact us through the channel indicated in Section 12 of this Policy. We will respond within a reasonable timeframe appropriate to the complexity of the request."
        },
        {
          title: "8. Account deletion",
          paragraphs: ["The user may request account deletion at any time through the contact channel provided. Upon confirmation, personal data will be deleted within a reasonable timeframe, subject to any applicable legal retention requirements."]
        },
        {
          title: "9. Use by minors",
          paragraphs: [
            "The application is intended for users aged 13 and above. The processing of data belonging to adolescents (aged 13 to 17) will prioritize the best interests of the minor, in accordance with Articles 14 and 17 of the LGPD and the Brazilian Statute of Children and Adolescents (ECA).",
            "Users under 18 must use the application with the knowledge and supervision of a legal guardian. Athena Studies may request guardian confirmation when deemed necessary.",
            "Parthenon Planner is not directed at children under 13 and does not intentionally collect data from such individuals."
          ]
        },
        {
          title: "10. Cookies",
          intro: "We may use cookies and similar technologies to ensure the proper functioning of the service and enhance user experience. The cookies we use fall into two categories:",
          listItems: [
            <><strong>Essential cookies</strong> — required for the app to function (e.g., maintaining an authenticated session). Disabling them will impair the service.</>,
            <><strong>Analytical cookies</strong> — used to understand how users interact with the app and improve their experience. These can be declined without affecting core functionality.</>
          ],
          footnote: "We do not use third-party cookies for advertising or commercial tracking purposes."
        },
        {
          title: "11. Changes to this policy",
          paragraphs: ["This Policy may be updated periodically. Material changes will be communicated to users by email or via an in-app notice. The most recent version will always be available on the website or app."]
        },
        {
          title: "12. Contact",
          contactLabel: "Support Channel — Data Protection (LGPD)"
        }
      ],
      lastUpdated: "Last updated: January 2026",
      footer: "© 2026 • MASTER YOUR FUTURE"
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans selection:bg-athena-coral selection:text-white transition-colors duration-300">

      {/* Header Minimalista */}
      <header className="fixed top-0 w-full z-[100] backdrop-blur-xl bg-white/70 dark:bg-slate-900/70 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="p-2 bg-athena-coral rounded-xl text-white shadow-lg group-hover:scale-110 transition-transform">
              <Bird size={24} />
            </div>
            <h1 className="text-xl font-black tracking-tighter text-athena-teal dark:text-white uppercase">
              Parthenon<span className="text-athena-coral">.</span>
            </h1>
          </Link>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setLanguage('pt')}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  language === 'pt'
                    ? 'bg-slate-100 dark:bg-slate-800 ring-2 ring-athena-blue dark:ring-blue-500 scale-105 shadow-sm'
                    : 'opacity-40 hover:opacity-100 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
                title="Português"
              >
                <img src="https://flagcdn.com/br.svg" alt="Português" className="w-5 h-5 rounded-full object-cover" />
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  language === 'en'
                    ? 'bg-slate-100 dark:bg-slate-800 ring-2 ring-athena-blue dark:ring-blue-500 scale-105 shadow-sm'
                    : 'opacity-40 hover:opacity-100 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
                title="English"
              >
                <img src="https://flagcdn.com/us.svg" alt="English" className="w-5 h-5 rounded-full object-cover" />
              </button>
            </div>

            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 shadow-sm hover:scale-110 active:scale-95 transition-all"
              title={language === 'pt' ? "Trocar Tema" : "Toggle Theme"}
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <Link
              to="/"
              className="px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-950 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:opacity-90 active:scale-95 transition-all flex items-center gap-2"
            >
              <ArrowLeft size={14} /> {t.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 rounded-[2.5rem] border-2 border-slate-200 dark:border-slate-800 p-8 md:p-16 shadow-2xl">

          <div className="flex items-center gap-4 mb-10 text-athena-teal">
            <ShieldCheck size={48} />
            <div>
              <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-slate-950 dark:text-white leading-none">{t.pageTitle}</h1>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mt-2">{t.subtitle}</p>
            </div>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-8 text-slate-700 dark:text-slate-300">
            <p className="text-lg font-bold leading-relaxed italic">{t.intro}</p>
            <p className="font-medium">{t.introConsent}</p>
            <div className="h-px bg-slate-100 dark:bg-slate-800 w-full" />

            {t.sections.map((section, idx) => {
              const s = section as any;

              // Seção de Contato
              if (s.contactLabel) {
                return (
                  <section key={idx} className="space-y-4">
                    <h2 className="text-xl font-black text-athena-teal dark:text-athena-coral uppercase tracking-tight">{s.title}</h2>
                    <div className="p-6 md:p-8 bg-athena-teal/5 dark:bg-athena-teal/10 rounded-3xl border-2 border-athena-teal/20 flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 text-center sm:text-left">
                      <div className="w-12 h-12 bg-athena-teal rounded-2xl flex items-center justify-center text-white shadow-lg shrink-0">
                        <Mail size={24} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-1">{s.contactLabel}</p>
                        <p className="text-base md:text-lg font-black text-athena-teal break-all">studiesathena2025@gmail.com</p>
                      </div>
                    </div>
                  </section>
                );
              }

              // Seção de Dados Coletados (com dataBlocks)
              if (s.dataBlocks) {
                return (
                  <section key={idx} className="space-y-4">
                    <h2 className="text-xl font-black text-athena-teal dark:text-athena-coral uppercase tracking-tight">{s.title}</h2>
                    <p className="font-medium">{s.intro}</p>
                    <div className="space-y-6 mt-4">
                      {s.dataBlocks.map((block: any, bi: number) => (
                        <div key={bi} className={`p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border-l-4 ${block.color === 'teal' ? 'border-athena-teal' : block.color === 'coral' ? 'border-athena-coral' : 'border-athena-gold'}`}>
                          <h3 className="text-xs font-black uppercase mb-3 text-slate-900 dark:text-white">{block.label}</h3>
                          <ul className="list-disc pl-5 space-y-2 text-sm font-bold">
                            {block.items.map((item: string, ii: number) => <li key={ii}>{item}</li>)}
                          </ul>
                          {block.footnote && (
                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-3">{block.footnote}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-xl border border-amber-200 dark:border-amber-900 flex items-center gap-3 mt-4">
                      <div className="text-amber-600 dark:text-amber-400 font-black text-sm uppercase">{s.noteLabel}</div>
                      <p className="text-xs font-bold text-amber-800 dark:text-amber-400">{s.note}</p>
                    </div>
                  </section>
                );
              }

              // Seção de Finalidade (grid de pills)
              if (s.purposeItems) {
                return (
                  <section key={idx} className="space-y-4">
                    <h2 className="text-xl font-black text-athena-teal dark:text-athena-coral uppercase tracking-tight">{s.title}</h2>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {s.purposeItems.map((item: string, i: number) => (
                        <li key={i} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/30 rounded-xl text-xs font-black uppercase">
                          <div className="w-2 h-2 rounded-full bg-athena-teal" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    {s.purposeFootnote && (
                      <p className="text-sm font-bold text-slate-500">{s.purposeFootnote}</p>
                    )}
                  </section>
                );
              }

              // Seção genérica
              return (
                <section key={idx} className="space-y-4">
                  <h2 className="text-xl font-black text-athena-teal dark:text-athena-coral uppercase tracking-tight">{s.title}</h2>
                  {s.highlight && (
                    <p className="font-medium text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-800 px-4 py-2 rounded-lg inline-block">{s.highlight}</p>
                  )}
                  {s.highlightSub && <p className="font-medium">{s.highlightSub}</p>}
                  {s.intro && <p className="font-medium">{s.intro}</p>}
                  {s.paragraphs && s.paragraphs.map((p: any, pi: number) => (
                    <p key={pi} className="font-medium">{p}</p>
                  ))}
                  {s.listItems && (
                    <ul className="list-disc pl-5 space-y-2 text-sm font-bold">
                      {s.listItems.map((item: any, i: number) => <li key={i}>{item}</li>)}
                    </ul>
                  )}
                  {s.footnote && (
                    <p className="text-sm font-bold text-slate-500">{s.footnote}</p>
                  )}
                </section>
              );
            })}

            <div className="h-px bg-slate-100 dark:bg-slate-800 w-full" />
            <p className="text-center text-[10px] font-black uppercase text-slate-400 tracking-[0.3em]">{t.lastUpdated}</p>
          </div>
        </div>
      </main>

      {/* Footer Minimalista */}
      <footer className="py-10 text-center border-t border-slate-200 dark:border-slate-800">
        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{t.footer}</p>
      </footer>
    </div>
  );
};